/**
 * Volume Role - Convert Pillar
 *
 * Conversão entre unidades de volume usando arquitetura hub-and-spoke.
 * Base: liter (L)
 *
 * @example
 * import { convertVolume, toBaseVolume, fromBaseVolume } from '@attrx/role-morphic/volume/convert';
 *
 * convertVolume('gallon_us', 'liter', 1);     // 3.785411784
 * toBaseVolume('milliliter', 500);            // 0.5
 * fromBaseVolume('gallon_us', 3.785411784);   // 1
 */

import { VOLUME_UNITS, type VolumeUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const VOLUME_BASE: VolumeUnit = "liter";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (liter)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em liter
 *
 * @example
 * toBaseVolume('milliliter', 500);     // 0.5
 * toBaseVolume('gallon_us', 1);        // 3.785411784
 */
export function toBaseVolume(variant: VolumeUnit, value: number): number {
  const unit = VOLUME_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown volume variant: ${variant}`);
  }

  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (liter) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em liter
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseVolume('milliliter', 0.5);        // 500
 * fromBaseVolume('gallon_us', 3.785411784); // 1
 */
export function fromBaseVolume(variant: VolumeUnit, baseValue: number): number {
  const unit = VOLUME_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown volume variant: ${variant}`);
  }

  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de volume
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertVolume('gallon_us', 'liter', 1);        // 3.785411784
 * convertVolume('milliliter', 'liter', 500);    // 0.5
 * convertVolume('cup_us', 'tablespoon_us', 1);  // 16
 */
export function convertVolume(
  from: VolumeUnit,
  to: VolumeUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base → to
  const baseValue = toBaseVolume(from, value);
  return fromBaseVolume(to, baseValue);
}

/**
 * Versão safe de convertVolume que retorna Result
 *
 * @example
 * tryConvertVolume('gallon_us', 'liter', 1);
 * // { ok: true, value: 3.785411784 }
 *
 * tryConvertVolume('invalid', 'liter', 1);
 * // { ok: false, error: 'Unknown volume variant: invalid' }
 */
export function tryConvertVolume(
  from: VolumeUnit,
  to: VolumeUnit,
  value: number
): Result<number> {
  try {
    const result = convertVolume(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasVolumeVariant(variant: string): variant is VolumeUnit {
  return variant in VOLUME_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getVolumeVariants(): VolumeUnit[] {
  return Object.keys(VOLUME_UNITS) as VolumeUnit[];
}
